import React, {RefObject, useCallback, useEffect, useRef, useState} from "react";

// A slim draggable scrollbar under a horizontally scrolling strip (trip photo
// previews, the Talking Fridge comic). The thumb mirrors the strip's scroll
// position; dragging it scrolls the strip.
export function useScrubber(scrollRef: RefObject<HTMLDivElement | null>, deps: unknown[] = []) {
    const trackRef = useRef<HTMLDivElement>(null);
    const [metrics, setMetrics] = useState({thumbWidth: 0, thumbLeft: 0, visible: false});
    const dragRef = useRef<{pointerId: number; startX: number; startScroll: number; trackW: number; scrollRange: number} | null>(null);

    const recompute = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        // hidden (display: none) while another page is showing: nothing to measure
        if (el.scrollWidth === 0) return;
        // the track spans the strip, so fall back to its width while the track is collapsed
        const trackW = trackRef.current?.clientWidth || el.clientWidth;
        const ratio = el.clientWidth / el.scrollWidth;
        const visible = ratio < 1;
        const thumbWidth = Math.max(20, Math.min(100, trackW * ratio * 0.5));
        const scrollRange = el.scrollWidth - el.clientWidth;
        const thumbLeft = scrollRange > 0 ? (el.scrollLeft / scrollRange) * (trackW - thumbWidth) : 0;
        setMetrics({thumbWidth, thumbLeft, visible});
    }, [scrollRef]);

    useEffect(() => {
        recompute();
        const el = scrollRef.current;
        if (!el) return;
        const ro = new ResizeObserver(recompute);
        ro.observe(el);
        el.addEventListener("scroll", recompute, {passive: true});
        // images load lazily: recompute when each finishes so scrollWidth is right
        el.querySelectorAll("img").forEach(img => {
            if (!img.complete) img.addEventListener("load", recompute, {once: true});
        });
        return () => {
            ro.disconnect();
            el.removeEventListener("scroll", recompute);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recompute, ...deps]);

    const onPointerDown = (e: React.PointerEvent) => {
        const el = scrollRef.current;
        const track = trackRef.current;
        if (!el || !track) return;
        e.preventDefault();
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
        dragRef.current = {
            pointerId: e.pointerId,
            startX: e.clientX,
            startScroll: el.scrollLeft,
            trackW: track.clientWidth,
            scrollRange: el.scrollWidth - el.clientWidth,
        };
    };
    const onPointerMove = (e: React.PointerEvent) => {
        const drag = dragRef.current;
        const el = scrollRef.current;
        if (!drag || !el || drag.pointerId !== e.pointerId) return;
        const travelable = drag.trackW - metrics.thumbWidth;
        if (travelable <= 0 || drag.scrollRange <= 0) return;
        el.scrollLeft = drag.startScroll + ((e.clientX - drag.startX) / travelable) * drag.scrollRange;
    };
    const onPointerUp = (e: React.PointerEvent) => {
        if (dragRef.current?.pointerId === e.pointerId) dragRef.current = null;
    };

    return {trackRef, metrics, handlers: {onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp}};
}

interface ScrubberProps {
    scrubber: ReturnType<typeof useScrubber>;
    // remove it entirely when the strip fits (rather than keeping its space)
    collapseWhenFits?: boolean;
}

export function Scrubber({scrubber, collapseWhenFits}: ScrubberProps) {
    const {trackRef, metrics, handlers} = scrubber;
    const stop = (e: React.MouseEvent) => e.stopPropagation();
    return (
        <div
            ref={trackRef}
            className="trip-scrubber-track"
            style={metrics.visible ? undefined : collapseWhenFits ? {display: "none"} : {visibility: "hidden"}}
            onClick={stop}
        >
            <div
                className="trip-scrubber-thumb"
                style={{width: metrics.thumbWidth, transform: `translateX(${metrics.thumbLeft}px)`}}
                {...handlers}
                onClick={stop}
            />
        </div>
    );
}
